import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Avatar,
  Tabs, Tab, TextField, Chip, Divider, Menu, MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { erc20Abi as ERC20_ABI, parseUnits, formatUnits, formatEther } from 'viem';
import { useAppKit } from '@reown/appkit/react';
 import { auth } from '../../components/firebase'; // путь как у вас в проекте
 import { useNavigate, useLocation } from 'react-router-dom';


/* ====== config ====== */
const CONTRACTS = {
  USDT: { token: '0xUSDT_TOKEN', staking: '0xYOUR_STAKING', decimals: 6 },
  USDC: { token: '0xUSDC_TOKEN', staking: '0xYOUR_STAKING', decimals: 6 },
  DAI:  { token: '0xDAI_TOKEN',  staking: '0xYOUR_STAKING', decimals: 18 },
  USDe: { token: '0xUSDE_TOKEN', staking: '0xYOUR_STAKING', decimals: 18 },
};
const PRUSD = { symbol: 'prUSD', decimals: 6 };
const STAKING_ABI = [
  { type: 'function', name: 'stake',    stateMutability: 'nonpayable', inputs: [{ name:'token', type:'address' }, { name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'stake',    stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'unstake',  stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'redeem',   stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'withdraw', stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'claim',    stateMutability: 'nonpayable', inputs: [], outputs: [] },
  { type: 'function', name: 'claim',    stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
];

const LOGO = {
  USDT: 'https://assets.coingecko.com/coins/images/325/standard/Tether.png?1696501400',
  USDC: 'https://assets.coingecko.com/coins/images/6319/standard/usdc.png?1696506694',
  DAI:  'https://assets.coingecko.com/coins/images/9956/standard/Badge_Dai.png?1696507713',
  USDe: 'https://coin-images.coingecko.com/coins/images/33613/standard/usde.png?1733810059',
  prUSD: 'https://assets.coingecko.com/coins/images/6319/standard/usdc.png?1696506694',
};
const STABLE_META = [
  { symbol: 'USDT', apy: 6.8, tvl: 245_000_000, lock: 'Flexible'  },
  { symbol: 'USDC', apy: 6.1, tvl: 198_000_000, lock: 'Flexible'  },
  { symbol: 'DAI',  apy: 5.7, tvl: 152_000_000, lock: 'Flexible'  },
  { symbol: 'USDe', apy: 7.2, tvl: 63_000_000,  lock: '30d lock'  },
];

// === BLACK GLASS THEME  ===
const R12 = '12px';
const DARK = '#0b0f15';
const GLASS = {
  surface: 'linear-gradient(180deg, rgba(17,22,30,.92) 0%, rgba(17,22,30,.82) 70%)',
  tile:    'rgba(21,27,38,.60)',
  border:  '1px solid rgba(255,255,255,.14)',
  borderStrong: '1px solid rgba(255,255,255,.28)',
  text:    '#eef1f6',
  subtext: '#b8c0cc',
};

const FEE_BPS = 110n; const BPS_DEN = 10_000n;
const CF_BASE = 'https://europe-west3-starwa-94ac5.cloudfunctions.net';

const accents = (sym) => ({
  ring:
    sym === 'USDT' ? '#14f195' :
    sym === 'USDC' ? '#2d74d7' :
    sym === 'DAI'  ? '#ffc107' : '#9aa3b2'
});

const fmtMoney = (n) => typeof n === 'number'
  ? (n >= 1e9 ? (n/1e9).toFixed(2)+'B' : (n/1e6).toFixed(2)+'M') : '—';
const fmt = (n, d = 2) => Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const pow10n = (p) => { let x = 1n; for (let i=0;i<p;i++) x*=10n; return x; };
const convertDecimals = (bn, fromDec, toDec) => fromDec===toDec ? bn : (fromDec<toDec ? bn*pow10n(toDec-fromDec) : bn/pow10n(fromDec-toDec));

const KPI_CHIP_SX = {
  border: GLASS.border,
  bgcolor: 'rgba(255,255,255,.08)',
  color: '#fff',
  height: { xs: 22, sm: 28 },                  
  '& .MuiChip-label': {
    px: { xs: 0.75, sm: 1 },                   
    fontSize: { xs: 10, sm: 12 },              
    lineHeight: 1,
    fontWeight: 800,
  },
};




export default function StakeCard({ onNotify }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { open } = useAppKit();

  const [mode, setMode] = useState('stake'); // stake | unstake | withdraw
  const [symbol, setSymbol] = useState('USDT');
  const [amount, setAmount] = useState('');

  const conf = useMemo(() => CONTRACTS[symbol], [symbol]);
  const tokenAddr   = conf?.token;
  const stakingAddr = conf?.staking;
  const [decimals, setDecimals] = useState(conf?.decimals ?? 6);

  const [underlyingBal, setUnderlyingBal] = useState(0n);
  const [prUSDBal]           = useState(0n);
  const [allowance, setAllowance] = useState(0n);

  const [gasUsd, setGasUsd] = useState(null);
  const [ethPrice, setEthPrice] = useState(3500);
  const [busy, setBusy] = useState(false);

  const meta = STABLE_META.find(m => m.symbol === symbol);

  // decimals (safety)
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        if (!publicClient || !tokenAddr) return;
        const dec = typeof conf?.decimals === 'number'
          ? conf.decimals
          : Number(await publicClient.readContract({ address: tokenAddr, abi: ERC20_ABI, functionName: 'decimals' }));
        if (!stop) setDecimals(dec);
      } catch {}
    })();
    return () => { stop = true; };
  }, [publicClient, tokenAddr, conf?.decimals]);

  // balances + allowance
  useEffect(() => {
    let stop = false;
    const load = async () => {
      if (!publicClient || !address || !tokenAddr || !stakingAddr) return;
      try {
        const [bal, alw] = await Promise.all([
          publicClient.readContract({ address: tokenAddr, abi: ERC20_ABI, functionName: 'balanceOf', args: [address] }),
          publicClient.readContract({ address: tokenAddr, abi: ERC20_ABI, functionName: 'allowance',  args: [address, stakingAddr] }),
        ]);
        if (!stop) { setUnderlyingBal(bal); setAllowance(alw); }
      } catch {}
    };
    load();
    const t = setInterval(load, 15_000);
    return () => { stop = true; clearInterval(t); };
  }, [publicClient, address, tokenAddr, stakingAddr]);

  // ETH price
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const r = await fetch(`${CF_BASE}/getCoinGeckoData?page=1&per_page=50`, { headers: { Accept: 'application/json' } });
        const data = await r.json();
        const eth = data?.ethereum?.usd;
        if (!stop && eth) setEthPrice(Number(eth));
      } catch {}
    })();
    return () => { stop = true; };
  }, []);

  // gas estimate
  useEffect(() => {
    const run = async () => {
      setGasUsd(null);
      if (!publicClient || !address || !stakingAddr || !tokenAddr) return;
      if (mode !== 'withdraw' && (!amount || Number(amount) <= 0)) return;
      try {
        const amt = parseUnits((amount || '0').trim() || '0', mode==='unstake' ? PRUSD.decimals : decimals);
        const fees = await publicClient.estimateFeesPerGas().catch(() => ({}));
        const price = fees?.maxFeePerGas ?? fees?.gasPrice; if (!price) return;
        let gas = 0n;

        if (mode === 'stake') {
          if (allowance < amt) {
            try {
              const g = await publicClient.estimateContractGas({
                account: address, address: tokenAddr, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddr, amt],
              }); gas += g;
            } catch {
              try {
                const g0 = await publicClient.estimateContractGas({
                  account: address, address: tokenAddr, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddr, 0n],
                });
                const g1 = await publicClient.estimateContractGas({
                  account: address, address: tokenAddr, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddr, amt],
                });
                gas += (g0 + g1);
              } catch {}
            }
          }
          try {
            const gStake = await publicClient.estimateContractGas({
              account: address, address: stakingAddr, abi: STAKING_ABI,
              functionName: 'stake', args: [tokenAddr, amt],
            }); gas += gStake;
          } catch {
            try {
              const g2 = await publicClient.estimateContractGas({
                account: address, address: stakingAddr, abi: STAKING_ABI,
                functionName: 'stake', args: [amt],
              }); gas += g2;
            } catch {}
          }
        } else if (mode === 'unstake') {
          for (const fn of ['unstake','redeem','withdraw']) {
            try {
              const g = await publicClient.estimateContractGas({
                account: address, address: stakingAddr, abi: STAKING_ABI, functionName: fn, args: [amt],
              }); gas += g; break;
            } catch {}
          }
        } else {
          try {
            const g = await publicClient.estimateContractGas({
              account: address, address: stakingAddr, abi: STAKING_ABI, functionName: 'claim', args: [parseUnits(amount || '0', decimals)],
            }); gas += g;
          } catch {
            try {
              const g2 = await publicClient.estimateContractGas({
                account: address, address: stakingAddr, abi: STAKING_ABI, functionName: 'claim', args: [],
              }); gas += g2;
            } catch {}
          }
        }

        if (gas > 0n) {
          const eth = Number(formatEther(gas * price));
          setGasUsd(eth * Number(ethPrice || 0));
        }
      } catch {}
    };
    run();
  }, [publicClient, address, tokenAddr, stakingAddr, amount, decimals, allowance, mode, ethPrice]);

  // flows
  const ensureAllowance = async (amt) => {
    if (allowance >= amt) return;
    try {
      const tx = await walletClient.writeContract({
        address: tokenAddr, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddr, amt], account: address,
      });
      onNotify?.('Approve sent…'); await publicClient.waitForTransactionReceipt({ hash: tx });
    } catch {
      const tx0 = await walletClient.writeContract({
        address: tokenAddr, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddr, 0n], account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: tx0 });
      const tx1 = await walletClient.writeContract({
        address: tokenAddr, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddr, amt], account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: tx1 });
    }
    const fresh = await publicClient.readContract({
      address: tokenAddr, abi: ERC20_ABI, functionName: 'allowance', args: [address, stakingAddr]
    });
    setAllowance(fresh);
  };

  const doStake = async () => {
    if (!walletClient || !address || !tokenAddr || !stakingAddr) return open?.({ view: 'Connect' });
    try {
      setBusy(true);
      const amt = parseUnits((amount || '0').trim() || '0', decimals);
      if (amt <= 0n) throw new Error('Amount must be greater than 0');
      await ensureAllowance(amt);
      try {
        const h = await walletClient.writeContract({
          address: stakingAddr, abi: STAKING_ABI, functionName: 'stake', args: [tokenAddr, amt], account: address,
        });
        onNotify?.('Stake sent…'); await publicClient.waitForTransactionReceipt({ hash: h });
      } catch {
        const h2 = await walletClient.writeContract({
          address: stakingAddr, abi: STAKING_ABI, functionName: 'stake', args: [amt], account: address,
        });
        onNotify?.('Stake sent…'); await publicClient.waitForTransactionReceipt({ hash: h2 });
      }
      onNotify?.('Stake confirmed ✅'); setAmount('');
    } catch (e) {
      onNotify?.(e?.shortMessage || e?.message || 'Stake failed');
    } finally { setBusy(false); }
  };

  const doUnstake = async () => {
    if (!walletClient || !address || !stakingAddr) return open?.({ view: 'Connect' });
    try {
      setBusy(true);
      const amt = parseUnits((amount || '0').trim() || '0', PRUSD.decimals);
      if (amt <= 0n) throw new Error('Amount must be greater than 0');
      let sent = false;
      for (const fn of ['unstake','redeem','withdraw']) {
        try {
          const h = await walletClient.writeContract({
            address: stakingAddr, abi: STAKING_ABI, functionName: fn, args: [amt], account: address,
          });
          onNotify?.('Unstake sent…'); await publicClient.waitForTransactionReceipt({ hash: h });
          sent = true; break;
        } catch {}
      }
      if (!sent) throw new Error('No unstake/redeem method');
      onNotify?.('Unstake confirmed'); setAmount('');
    } catch (e) {
      onNotify?.(e?.shortMessage || e?.message || 'Unstake failed');
    } finally { setBusy(false); }
  };

  const doWithdraw = async () => {
    if (!walletClient || !address || !stakingAddr) return open?.({ view: 'Connect' });
    try {
      setBusy(true);
      const amt = parseUnits((amount || '0').trim() || '0', decimals);
      try {
        const h = await walletClient.writeContract({
          address: stakingAddr, abi: STAKING_ABI, functionName: 'claim', args: [amt], account: address,
        });
        onNotify?.('Withdraw sent…'); await publicClient.waitForTransactionReceipt({ hash: h });
      } catch {
        const h2 = await walletClient.writeContract({
          address: stakingAddr, abi: STAKING_ABI, functionName: 'claim', args: [], account: address,
        });
        onNotify?.('Withdraw sent…'); await publicClient.waitForTransactionReceipt({ hash: h2 });
      }
      onNotify?.('Withdraw confirmed ✅'); setAmount('');
    } catch (e) {
      onNotify?.(e?.shortMessage || e?.message || 'Withdraw failed');
    } finally { setBusy(false); }
  };

  const amountIn = (() => {
    try { return parseUnits((amount||'0').trim()||'0', mode==='unstake' ? PRUSD.decimals : decimals); }
    catch { return 0n; }
  })();

  const stakeReceive_prUSD = (() => {
    if (mode !== 'stake' || amountIn === 0n) return '0';
    const toPr = convertDecimals(amountIn, decimals, PRUSD.decimals);
    const net  = (toPr * (BPS_DEN - FEE_BPS)) / BPS_DEN;
    return formatUnits(net, PRUSD.decimals);
  })();

  const unstakeReceive_base = (() => {
    if (mode !== 'unstake' || amountIn === 0n) return '0';
    const toBase = convertDecimals(amountIn, PRUSD.decimals, decimals);
    const net    = (toBase * (BPS_DEN - FEE_BPS)) / BPS_DEN;
    return formatUnits(net, decimals);
  })();

  const canSubmit = isConnected && !busy && (mode === 'withdraw' ? true : (amount && Number(amount) > 0));
  const accent = accents(symbol);

  return (
    <Card sx={{
      maxWidth: 560, mx: 'auto', borderRadius: R12,
      background: GLASS.surface, border: GLASS.border, color: GLASS.text,
      boxShadow: '0 12px 32px rgba(2,8,23,.45)', backdropFilter: 'blur(10px)',
      
      '& .MuiOutlinedInput-root': {
        background: 'rgba(255,255,255,.08)', color: GLASS.text, borderRadius: R12,
        '& fieldset': { borderColor: 'rgba(255,255,255,.18)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,.26)' },
        '&.Mui-focused fieldset': { borderColor: accent.ring },
      },
      '& .MuiInputBase-input': { color: GLASS.text },
      '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,.55)' },
    }}>
      <CardContent sx={{ p: { xs: 1.75, md: 2 } }}>
        {/* KPI + token selection*/}
        <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: { xs: .5, sm: .75 },
    mb: 1,
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }}
>
  {/* The selection button stretches and doesn't break. */}
  <TokenSelect
  value={symbol}
  onChange={setSymbol}
  sx={{
    // It stretches only on mobile, on sm+ it’s natural width
    flex: { xs: '1 1 auto', sm: '0 0 auto' },
    width: { xs: '100%', sm: 'auto' },
    minWidth: { xs: 0, sm: 132 },

    // Alignment: stretch the content on mobile, compact on desktop
    justifyContent: { xs: 'space-between', sm: 'flex-start' },
    px: { xs: 1, sm: 1.25 },

    // a neat gap between the icon and the text on the desktop
    gap: { xs: 0, sm: .75 },

    // so that the contents do not transfer and do not break the row
    whiteSpace: 'nowrap',
  }}
/>


  {/* compact chips on xs */}
  <Chip size="small" label={`APY ${meta?.apy?.toFixed(1)}%`} sx={KPI_CHIP_SX} />
  <Chip size="small" label={`TVL $${fmtMoney(meta?.tvl || 0)}`} sx={KPI_CHIP_SX} />
  <Chip size="small" label={meta?.lock || 'Flexible'} sx={KPI_CHIP_SX} />
</Box>


        {/* Tabs */}
        <Tabs
          value={mode}
          onChange={(_, v) => { setMode(v); setAmount(''); }}
          sx={{
            minHeight: 36, height: 36, mb: 1,
            '& .MuiTab-root': { minHeight: 36, height: 36, textTransform: 'none', fontWeight: 800, color: GLASS.text },
            '& .MuiTabs-indicator': { backgroundColor: accent.ring },
          }}
        >
          <Tab value="stake" label="Stake" />
          <Tab value="unstake" label="Unstake" />
          <Tab value="withdraw" label="Withdraw" />
        </Tabs>

        {/* FROM */}
        <Box sx={{ p: 1.25, borderRadius: R12, border: GLASS.border, background: GLASS.tile, mb: .75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
            <Avatar src={mode==='unstake' ? LOGO.prUSD : LOGO[symbol]} sx={{ width: 22, height: 22 }} />
            <Typography sx={{ fontWeight: 800 }}>
              {mode==='unstake' ? PRUSD.symbol : symbol}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: GLASS.subtext }}>
              Balance:&nbsp;
              {mode==='unstake' ? formatUnits(prUSDBal, PRUSD.decimals) : formatUnits(underlyingBal, decimals)}
            </Typography>
            {mode !== 'withdraw' && (
              <Button
                size="small"
                onClick={() => setAmount(
                  mode==='unstake' ? formatUnits(prUSDBal, PRUSD.decimals) : formatUnits(underlyingBal, decimals)
                )}
                sx={{ ml: 1, borderRadius: R12, textTransform: 'none' }}
                variant="outlined"
              >
                Max
              </Button>
            )}
          </Box>

          <TextField
            fullWidth
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            sx={{ mt: .75 }}
          />
        </Box>

        {/* arrow */}
        {mode !== 'withdraw' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: .25 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%',
              border: `2px solid ${accent.ring}`,
              display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,.05)'
            }}>
              <ArrowDownwardIcon sx={{ fontSize: 18, color: accent.ring }} />
            </Box>
          </Box>
        )}

        {/* YOU RECEIVE */}
        {mode !== 'withdraw' && (
          <Box sx={{ p: 1.25, borderRadius: R12, border: GLASS.border, background: GLASS.tile, mb: .5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5 }}>
              <Avatar src={mode==='stake' ? LOGO.prUSD : LOGO[symbol]} sx={{ width: 22, height: 22 }} />
              <Typography sx={{ fontWeight: 800 }}>
                {mode==='stake' ? PRUSD.symbol : symbol}
              </Typography>
              <Box sx={{ flex: 1 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {mode==='stake' ? stakeReceive_prUSD : unstakeReceive_base}
            </Typography>
            <Typography variant="caption" sx={{ color: GLASS.subtext }}>
              Includes 1.1% fee
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mt: .75, mb: .25 }}>
          <Typography variant="caption" sx={{ color: GLASS.subtext }}>Rewards Eligible</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: GLASS.subtext }}>
            Estimated Gas: {gasUsd != null ? `$${fmt(gasUsd, 2)}` : '—'}
          </Typography>
        </Box>

        {/* button*/}
       {!isConnected ? (
          <Button
            fullWidth
            variant="contained"
           onClick={() => {
  const u = auth.currentUser;
  if (!u) {
    navigate('/login?next=/stake', { state: { background: location } });
    return;
  }
              if (!u.emailVerified) {
   navigate('/verify-email', { state: { email: u.email }, replace: true });
   return;
 }
              open?.({ view: 'Connect' });
            }}
            sx={{
              mt: .5, height: 44, borderRadius: 999, textTransform: 'none', fontWeight: 900,
              background: `linear-gradient(90deg, ${accents(symbol).ring} 0%, #ffffff 130%)`,
              color: DARK, '&:hover': { filter: 'brightness(1.05)' }
            }}
          >
            Connect Wallet
          </Button>
        ) : (
          <Button
            fullWidth variant="contained" disabled={!canSubmit}
            onClick={mode==='stake' ? doStake : mode==='unstake' ? doUnstake : doWithdraw}
            sx={{
              mt: .5, height: 44, borderRadius: 999, textTransform: 'none', fontWeight: 900,
              background: `linear-gradient(90deg, ${accents(symbol).ring} 0%, #ffffff 130%)`,
              color: DARK, '&:hover': { filter: 'brightness(1.05)' }
            }}
          >
            {busy
              ? (mode==='stake' ? 'Staking…' : mode==='unstake' ? 'Unstaking…' : 'Withdrawing…')
              : (mode==='stake' ? 'Stake'     : mode==='unstake' ? 'Unstake'     : 'Withdraw')}
          </Button>
        )}

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,.12)' }} />
        <Typography variant="caption" sx={{ color: GLASS.subtext }}>
          Fee: 1.1% • Compounding: monthly • Network: Ethereum
        </Typography>
      </CardContent>
    </Card>
  );
}

function TokenSelect({ value, onChange, sx }) {
  const [anchorEl, setAnchorEl] = useState(null);

  
  const isValidAnchor =
    !!(anchorEl && document.body.contains(anchorEl) && anchorEl.offsetParent !== null);

  
  useEffect(() => {
    if (anchorEl && !isValidAnchor) setAnchorEl(null);
  }, [anchorEl, isValidAnchor]);

  return (
    <>
      <Button
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<Avatar src={LOGO[value]} sx={{ width: 22, height: 22 }} />}
        endIcon={<ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          borderRadius: R12,
          border: GLASS.border,
          color: GLASS.text,
          bgcolor: 'rgba(255,255,255,.08)',
          '&:hover': { bgcolor: 'rgba(255,255,255,.12)' },
          display: 'flex',
          alignItems: 'center',
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { m: 0 },
          ...sx, 
        }}
      >
        {value}
      </Button>

      <Menu
        anchorEl={isValidAnchor ? anchorEl : null}
        open={isValidAnchor}
        onClose={() => setAnchorEl(null)}
        keepMounted
        PaperProps={{
          sx: {
            background: 'rgba(17,22,30,.95)',
            color: GLASS.text,
            border: GLASS.border,
            backdropFilter: 'blur(6px)'
          }
        }}
      >
        {['USDT','USDC','DAI','USDe'].map((s) => (
          <MenuItem
            key={s}
            onClick={() => { onChange(s); setAnchorEl(null); }}
            selected={s === value}
          >
            <Avatar src={LOGO[s]} sx={{ width: 20, height: 20, mr: 1 }} />
            {s}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

